import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { generateReport, getFreePlans, FreePlan } from '../services/InfluencersCRUD';
import { toast } from 'react-toastify';
import DateRangeSelector from '../../../components/DateRangeSelector';

export default function InfluencerStats() {
  const intl = useIntl();
  const [loading, setLoading] = useState<boolean>(false);
  const [since, setSince] = useState<Date>();
  const [until, setUntil] = useState<Date>();
  const [code, setCode] = useState<string>('');

  const [freePlansLoading, setFreePlansLoading] = useState<boolean>(false);
  const [freePlans, setFreePlans] = useState<FreePlan[] | null>(null);

  async function onGenerateReport() {
    try {
      setLoading(true);
      await generateReport({ since, until, code });
      toast.success(
        intl.formatMessage({ id: 'influencerStats.reportGenerated' })
      );
    } catch (error) {
      console.warn(error);
      toast.error(
        intl.formatMessage({ id: 'influencerStats.reportGenerationError' })
      );
    } finally {
      setLoading(false);
    }
  }

  async function onLoadFreePlans() {
    try {
      setFreePlansLoading(true);
      const { data } = await getFreePlans();
      setFreePlans(data);
    } catch (error) {
      console.warn(error);
      toast.error(
        intl.formatMessage({ id: 'influencerStats.freePlansError' })
      );
    } finally {
      setFreePlansLoading(false);
    }
  }

  function onDownloadCSV() {
    if (!freePlans) return;
    const header = 'Código Plan,Email Usuario,ID Usuario,ID Plan';
    const rows = freePlans.map(
      (p) => `${p.plan_code},${p.user_email},${p.user_id},${p.plan_id}`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'planes-gratuitos-influencers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  return (
    <div>
      <h1 className="mb-8">
        {intl.formatMessage({ id: 'influencerStats.title' })}
      </h1>
      <div className="d-flex gap-4 align-items-center">
        <DateRangeSelector
          initialSince={since}
          initialUntil={until}
          onSelected={(since, until) => {
            setSince(since);
            setUntil(until);
          }}
        />
        <div className="form-group">
          <label htmlFor="code" className="form-label">
            {intl.formatMessage({ id: 'influencerStats.codeLabel' })}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder={intl.formatMessage({
              id: 'influencerStats.codePlaceholder',
            })}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <button
          className="btn btn-primary mt-8"
          onClick={onGenerateReport}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'influencerStats.generate' })}
        </button>
      </div>

      <hr className="my-10" />

      <h2 className="mb-6">
        {intl.formatMessage({ id: 'influencerStats.freePlansTitle' })}
      </h2>
      <div className="d-flex gap-4 align-items-center mb-6">
        <button
          className="btn btn-primary"
          onClick={onLoadFreePlans}
          disabled={freePlansLoading}
        >
          {freePlansLoading
            ? intl.formatMessage({ id: 'influencerStats.freePlansLoading' })
            : intl.formatMessage({ id: 'influencerStats.freePlansLoad' })}
        </button>
        {freePlans && freePlans.length > 0 && (
          <button className="btn btn-secondary" onClick={onDownloadCSV}>
            {intl.formatMessage({ id: 'influencerStats.freePlansDownload' })}
          </button>
        )}
      </div>
      {freePlans !== null && (
        freePlans.length === 0 ? (
          <p className="text-muted">
            {intl.formatMessage({ id: 'influencerStats.freePlansEmpty' })}
          </p>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>{intl.formatMessage({ id: 'influencerStats.freePlansPlanCode' })}</th>
                  <th>{intl.formatMessage({ id: 'influencerStats.freePlansUserEmail' })}</th>
                </tr>
              </thead>
              <tbody>
                {freePlans.map((plan) => (
                  <tr key={plan.plan_id}>
                    <td>
                      <Link to={`/user/${plan.user_id}/profile`}>
                        {plan.plan_code}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/user/${plan.user_id}/profile`}>
                        {plan.user_email}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
